import type { FetchNextPageOptions } from "@tanstack/react-query"
import { FormattedMessage } from "react-intl"

import { FullLoader } from "@/components/FullLoader"
import { IntersectSpy } from "@/components/IntersectSpy"
import type { SharedDataJsonldReadCollectionSharedData } from "@/networking/api"
import { Box, Spinner } from "@chakra-ui/react"
import { useMemo } from "react"

type Props = React.PropsWithChildren<{
  currentSearchTerm?: string
  items?: SharedDataJsonldReadCollectionSharedData[]
  isFetchingData: boolean
  fetchNextPage: (options?: FetchNextPageOptions) => Promise<unknown>
  hasNextPage?: boolean
}>

export const TableLayout: React.FC<Props> = ({
  currentSearchTerm,
  items,
  isFetchingData,
  fetchNextPage,
  hasNextPage,
  children,
}) => {
  const onIntersect = () => {
    if (!isFetchingData && hasNextPage) {
      void fetchNextPage()
    }
  }

  const hasResult = useMemo(() => (items?.length ?? 0) > 0, [items])
  const hasSearchParam = useMemo(
    () => currentSearchTerm !== "" && currentSearchTerm !== undefined,
    [currentSearchTerm]
  )

  return (
    <div>
      {hasResult && (
        <>
          {children}
          <IntersectSpy onIntersect={onIntersect} />
        </>
      )}
      {!isFetchingData ? (
        !hasResult &&
        (hasSearchParam ? (
          <FormattedMessage id="search.noResult" values={{ currentSearchTerm }} />
        ) : (
          <FormattedMessage id="noResult" />
        ))
      ) : hasResult ? (
        <Box textAlign="center" my={8}>
          <Spinner />
        </Box>
      ) : (
        <FullLoader noAppearDelay={items !== undefined} />
      )}
    </div>
  )
}
